import React, { useRef, useState, useCallback } from "react";
import Popup from "../../../common/Popup/Popup.js";
import SignatureField from "../../../common/FormFields/SignatureField";
import Button from "../../../common/Buttons/Button";
import "../../../common/Loader.css";

/**
 * Complete Training — trainer password + trainer & trainee signatures (live / training checkmark flow).
 */
const CompleteTrainingModal = ({
  closeModal,
  partNumber = "",
  revision = "",
  trainerName = "",
  traineeName = "",
  confirmLabel = "Confirm",
  closeOnSuccess = true,
  onConfirm,
}) => {
  const [trainerPassword, setTrainerPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const trainerSigRef = useRef(null);
  const traineeSigRef = useRef(null);

  const handleConfirm = useCallback(async () => {
    const pw = trainerPassword.trim();
    if (!pw) {
      onConfirm?.({ error: "Trainer password is required." });
      return;
    }
    if (trainerSigRef.current?.isEmpty?.()) {
      onConfirm?.({ error: "Trainer signature is required." });
      return;
    }
    if (traineeSigRef.current?.isEmpty?.()) {
      onConfirm?.({ error: "Trainee signature is required." });
      return;
    }
    const trainerCanvas = trainerSigRef.current?.getCanvas?.();
    const traineeCanvas = traineeSigRef.current?.getCanvas?.();
    const trainerSignature = trainerCanvas?.toDataURL?.("image/png") ?? "";
    const traineeSignature = traineeCanvas?.toDataURL?.("image/png") ?? "";
    setIsSubmitting(true);
    try {
      const result = await onConfirm?.({
        trainerPassword: pw,
        trainerSignature,
        traineeSignature,
      });
      // Wrong password / API failure: parent resolves { error } — do not close; stay on this modal.
      if (result?.error) {
        return;
      }
      if (closeOnSuccess) {
        closeModal?.();
      }
    } catch {
      /* Parent rejected or threw; keep modal open on same page */
    } finally {
      setIsSubmitting(false);
    }
  }, [trainerPassword, onConfirm, closeModal, closeOnSuccess]);

  const handleCloseModal = useCallback(
    (...args) => {
      if (isSubmitting) return;
      closeModal?.(...args);
    },
    [isSubmitting, closeModal],
  );

  const title = `Complete Training for ${partNumber || "—"} Rev ${revision || "—"}`;

  /** Keeps signature pads the same width as the password field (avoid canvas min-content overflow). */
  const fieldColumnStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
  };

  const signatureFieldStyle = {
    width: "100%",
    maxWidth: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    marginLeft:"-1px",
  };

  /** Do not use negative margin here — it shifts the pad past the container and clips the left border (with overflow). */
  const signaturePadCanvasStyle = {
    maxWidth: "100%",
    boxSizing: "border-box",
  };

  /** Slightly smaller than default `sm` — matches mockup (compact Close + Confirm). */
  const footerBtnStyle = {
    padding: "5px 14px",
    fontSize: "12px",
    minHeight: "28px",
    borderRadius: "4px",
  };

  return (
    <>
      {isSubmitting && (
        <div className="loader-overlay" aria-live="polite" aria-busy="true">
          <div className="loader-container">
            <div className="loader-spinner" />
            <div className="loader-text">Please wait…</div>
          </div>
        </div>
      )}
    <Popup
      closeModal={handleCloseModal}
      title={title}
      headerStyle={{
        textAlign: "left",
        borderBottom: "1px solid #dee2e6",
        paddingBottom: "14px",
      }}
      titleStyle={{
        fontSize: "1rem",
        fontWeight: 600,
        lineHeight: 1,
        textAlign: "left",
      }}
      width="min(560px, 96vw)"
      shouldScroll
    >
      <div
        style={{
          padding: "8px 20px 24px",
          maxHeight: "min(78vh, 900px)",
          overflowY: "auto",
          ...fieldColumnStyle,
        }}
      >
        <div style={{ marginBottom: "18px" }}>
          <span style={{ fontSize: "12px", color: "#212529" }}>
            <strong>Trainer:</strong>{" "}
            <span style={{ textDecoration: "underline", fontWeight: 600 }}>
              {trainerName || "—"}
            </span>
          </span>
        </div>

        <div style={{ marginBottom: "12px", ...fieldColumnStyle }}>
          <label
            style={{
              display: "block",
              fontSize: "12px",
              marginBottom: "6px",
              color: "#212529",
            }}
          >
            Trainer&apos;s Password
            <span className="required" style={{ color: "#0d6efd", marginLeft: "0.5px" }}>
              *
            </span>
          </label>
          <input
            type="password"
            autoComplete="new-password"
            value={trainerPassword}
            onChange={(e) => setTrainerPassword(e.target.value)}
            placeholder="Trainer Enters Password Here"
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: "10px 12px",
              border: "1px solid #CED4DA",
              borderRadius: "5px",
              fontSize: "12px",
            }}
          />
        </div>

        <div style={{ marginBottom: "20px", ...fieldColumnStyle }}>
          <SignatureField
            ref={trainerSigRef}
            label="Trainer's Signature"
            required
            width="100%"
            height={120}
            style={signatureFieldStyle}
            canvasStyle={signaturePadCanvasStyle}
            labelStyle={{ fontSize: "12px", fontWeight: 600 }}
          />
        </div>

        <div style={{ marginBottom: "18px" }}>
          <span style={{ fontSize: "12px", color: "#212529" }}>
            <strong>Trainee:</strong>{" "}
            <span style={{ textDecoration: "underline", fontWeight: 600 }}>
              {traineeName || "—"}
            </span>
          </span>
        </div>

        <div style={{ marginBottom: "8px", ...fieldColumnStyle }}>
          <SignatureField
            ref={traineeSigRef}
            label="Trainee's Signature"
            required
            width="100%"
            height={120}
            style={signatureFieldStyle}
            canvasStyle={signaturePadCanvasStyle}
            labelStyle={{ fontSize: "12px", fontWeight: 600 }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "10px",
            marginTop: "28px",
            paddingTop: "16px",
            borderTop: "1px solid #e9ecef",
          }}
        >
          <Button
            type="button"
            variant="gray"
            size="sm"
            onClick={() => handleCloseModal()}
            disabled={isSubmitting}
            style={{ ...footerBtnStyle, color: "#ffffff" }}
          >
            Close
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleConfirm}
            disabled={isSubmitting}
            style={{ ...footerBtnStyle, color: "#ffffff" }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Popup>
    </>
  );
};

export default CompleteTrainingModal;
